import { useEditor, EditorContent, useEditorState, type Editor } from "@tiptap/react";
import { useTranslation } from "react-i18next";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { Bold, Italic, List, ListOrdered, Image as ImageIcon, Link as LinkIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import DOMPurify from "isomorphic-dompurify";
import { useRef, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { convertImageToWebP } from "@/lib/image";
import { cn } from "@/lib/utils";
import {
  applyTextSize,
  ExitHeadingOnEnter,
  getActiveTextSize,
  normalizePastedHeadingHtml,
  type TextSize,
} from "@/lib/rich-text-size";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  variant?: "default" | "panel";
}

const SIZE_OPTIONS: { id: TextSize; key: "normal" | "subtitle" | "title" }[] = [
  { id: "normal", key: "normal" },
  { id: "subtitle", key: "subtitle" },
  { id: "title", key: "title" },
];

function keepEditorSelection(e: React.MouseEvent) {
  e.preventDefault();
}

function ToolbarButton({
  onClick,
  active,
  label,
  children,
  pill,
}: {
  onClick: () => void;
  active?: boolean;
  label: string;
  children: React.ReactNode;
  pill?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      aria-label={label}
      aria-pressed={!!active}
      title={label}
      onMouseDown={keepEditorSelection}
      onClick={onClick}
      className={cn(
        "h-8 w-8 p-0 transition-transform duration-150 ease-out active:scale-[0.97]",
        pill && "rounded-full",
        active && (pill ? "bg-background shadow-sm" : "bg-muted"),
      )}
    >
      {children}
    </Button>
  );
}

const MenuBar = ({
  editor,
  onImageUpload,
  variant = "default",
}: {
  editor: Editor | null;
  onImageUpload: (file: File) => Promise<string | null>;
  variant?: "default" | "panel";
}) => {
  const { t } = useTranslation("producerMessages");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const active = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      if (!current) {
        return {
          bold: false,
          italic: false,
          bullet: false,
          ordered: false,
          link: false,
          size: "normal" as TextSize,
        };
      }
      return {
        bold: current.isActive("bold"),
        italic: current.isActive("italic"),
        bullet: current.isActive("bulletList"),
        ordered: current.isActive("orderedList"),
        link: current.isActive("link"),
        size: getActiveTextSize(current),
      };
    },
  });

  if (!editor || !active) {
    return null;
  }

  const addImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await onImageUpload(file);
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL do Link:", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className={cn(
      "flex flex-wrap items-center gap-0.5",
      variant === "panel"
        ? "px-3 py-2"
        : "p-1.5 border border-input bg-muted/40 rounded-t-md",
    )}>
      <div role="group" aria-label={t("richEditor.size.group")} className="flex items-center">
        {SIZE_OPTIONS.map((option) => {
          const isActive = active.size === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-label={t(`richEditor.size.${option.key}`)}
              aria-pressed={isActive}
              title={t(`richEditor.size.${option.key}`)}
              onMouseDown={keepEditorSelection}
              onClick={() => applyTextSize(editor, option.id)}
              className={cn(
                "h-8 px-2.5 text-xs font-medium transition-[color,background-color,transform] duration-150 ease-out active:scale-[0.97]",
                variant === "panel" ? "rounded-full" : "rounded-md",
                isActive ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-foreground/5",
              )}
            >
              {t(`richEditor.size.${option.key}`)}
            </button>
          );
        })}
      </div>

      <span className="mx-1 h-4 w-px bg-border/80" aria-hidden />

      <ToolbarButton
        pill={variant === "panel"}
        label={t("richEditor.toolbar.bold")}
        active={active.bold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        pill={variant === "panel"}
        label={t("richEditor.toolbar.italic")}
        active={active.italic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        pill={variant === "panel"}
        label={t("richEditor.toolbar.bulletList")}
        active={active.bullet}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        pill={variant === "panel"}
        label={t("richEditor.toolbar.orderedList")}
        active={active.ordered}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        pill={variant === "panel"}
        label={t("richEditor.toolbar.link")}
        active={active.link}
        onClick={setLink}
      >
        <LinkIcon className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarButton pill={variant === "panel"} label={t("richEditor.toolbar.image")} onClick={() => fileInputRef.current?.click()}>
        <ImageIcon className="w-4 h-4" />
      </ToolbarButton>
      <input
        type="file"
        accept="image/png, image/jpeg, image/webp"
        className="hidden"
        ref={fileInputRef}
        onChange={addImage}
      />
    </div>
  );
};

export const RichTextEditor = ({ value, onChange, variant = "default" }: RichTextEditorProps) => {
  const isPanel = variant === "panel";
  const { t } = useTranslation("producerMessages");
  const { toast } = useToast();

  const sanitize = (html: string) => DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'strong', 'em', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'img', 'br'],
    ALLOWED_ATTR: ['href', 'src', 'target', 'rel', 'class', 'alt', 'title']
  });

  // Word/Docs colam marcadores de lista com a fonte Symbol, que sem a fonte
  // vira o caractere "§" (U+00A7). Também aparecem ·, •, ▪, o etc. como texto.
  // Converte parágrafos iniciados por esses marcadores em <ul><li> reais.
  const BULLET_MARKER = /^\s*(?:§|•|·|▪|◦|‣|⁃|o\s|-\s)\s*/;

  const fixWordBullets = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const blocks = Array.from(doc.body.querySelectorAll('p'));

    let currentList: HTMLUListElement | null = null;
    for (const p of blocks) {
      const text = p.textContent || '';
      if (BULLET_MARKER.test(text)) {
        // remove o marcador do início preservando o restante do texto
        p.textContent = text.replace(BULLET_MARKER, '').trim();
        const li = doc.createElement('li');
        li.innerHTML = p.innerHTML;
        if (!currentList) {
          currentList = doc.createElement('ul');
          p.replaceWith(currentList);
        } else {
          p.remove();
        }
        currentList.appendChild(li);
      } else {
        currentList = null;
      }
    }

    return doc.body.innerHTML;
  };

  const handleImageUpload = async (file: File): Promise<string | null> => {
    try {
      let processedFile = file;
      try {
        processedFile = await convertImageToWebP(file, { maxWidth: 1200, quality: 0.8 });
      } catch (err) {
        console.error("Erro ao converter imagem do editor para WebP:", err);
      }

      if (processedFile.size > 5 * 1024 * 1024) {
        toast({ title: t("richEditor.toasts.errorTitle", "Erro"), description: t("richEditor.toasts.maxSizeError", "A imagem deve ter no máximo 5MB"), variant: "destructive" });
        return null;
      }

      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.webp`;

      const { error } = await supabase.storage
        .from('event-descriptions')
        .upload(fileName, processedFile, {
          contentType: 'image/webp',
          upsert: false,
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      const { data: publicUrlData } = supabase.storage
        .from('event-descriptions')
        .getPublicUrl(fileName);

      return publicUrlData.publicUrl;
    } catch (error: any) {
      console.error('Image upload error:', error);
      toast({ title: t("richEditor.toasts.errorTitle", "Erro"), description: error?.message || t("richEditor.toasts.uploadError", "Falha ao fazer upload da imagem"), variant: "destructive" });
      return null;
    }
  };

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [2, 3] },
    }),
    ExitHeadingOnEnter,
    Image.configure({
      HTMLAttributes: {
        class: 'rounded-md max-w-full h-auto my-4',
      },
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'text-primary underline underline-offset-4',
      },
    }),
  ], []);

  const editor = useEditor({
    extensions,
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const sanitized = sanitize(html);
      // Only call onChange if the sanitized content actually changed
      // comparing with the current prop value to avoid loops
      if (sanitized !== value) {
        onChange(sanitized);
      }
    },
    editorProps: {
      attributes: {
        class: isPanel
          ? "th-rich-text prose dark:prose-invert focus:outline-none min-h-[220px] px-5 py-4 border-0 rounded-none bg-transparent max-w-none"
          : "th-rich-text prose dark:prose-invert focus:outline-none min-h-[200px] p-4 border border-input rounded-b-md bg-background max-w-none",
      },
      transformPastedHTML: (html) => normalizePastedHeadingHtml(html),
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);

        for (const item of items) {
          if (item.type.indexOf('image') === 0) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageUpload(file).then(url => {
                if (url) {
                  const { schema } = view.state;
                  const node = schema.nodes.image.create({ src: url });
                  const transaction = view.state.tr.replaceSelectionWith(node);
                  view.dispatch(transaction);
                }
              });
              return true;
            }
          }
        }

        // Cola de Word/Docs: converte marcadores "§"/bullets soltos em listas reais
        const html = event.clipboardData?.getData('text/html');
        if (html && BULLET_MARKER.test(html.replace(/<[^>]+>/g, ''))) {
          event.preventDefault();
          editor?.commands.insertContent(sanitize(fixWordBullets(normalizePastedHeadingHtml(html))));
          return true;
        }

        return false;
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    // Only update content if it's different to prevent infinite loops
    // Compare with current editor content
    const currentContent = editor.getHTML();
    if (value !== currentContent && value !== sanitize(currentContent)) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  return (
    <div className={cn(
      "w-full",
      isPanel && "overflow-hidden rounded-[22px] bg-secondary/50",
    )}>
      <MenuBar editor={editor} onImageUpload={handleImageUpload} variant={variant} />
      {isPanel && <div className="mx-3 h-px bg-border/50" />}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
